import { toast } from '@/components/common/toast'
import { EApiVersion } from '@/types/api'
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { __cAuthService } from './__c.auth.service'

export interface ApiResponse<T = unknown> {
  data?: T
  message: string
  status: number
  state: boolean
  error?: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: Record<string, unknown>,
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

class HttpClient {
  private client: AxiosInstance
  private useProxy: boolean
  private isLogout: boolean = false

  constructor(version?: EApiVersion, useProxy: boolean = true) {
    this.useProxy = useProxy
    this.client = axios.create({
      baseURL: '',
      timeout: 30000,
      headers: {
        Accept: 'application/json',
      },
      withCredentials: true,
    })

    if (version) {
      this.client.defaults.headers.version = version
    }

    this.setupInterceptors()
  }

  private buildURL(endpoint: string): string {
    if (!this.useProxy) {
      return endpoint
    }

    const cleanEndpoint = endpoint.startsWith('/')
      ? endpoint.slice(1)
      : endpoint
    return new URL(
      `/api/proxy/${cleanEndpoint}`,
      window.location.origin
    ).toString()
  }

  private setupInterceptors() {
    this.client.interceptors.request.use((config) => {
      // Xử lý FormData - không set Content-Type để browser tự động thêm boundary
      if (config.data instanceof FormData) {
        // Xóa Content-Type nếu có để browser tự động set với boundary
        if (config.headers && config.headers['Content-Type']) {
          delete config.headers['Content-Type']
        }
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.handleResponseError(error)
    )
  }

  private handleResponseError(error: AxiosError) {
    // handle 401 error call logout service
    if (error.response?.status === 401) {
      if (this.isLogout) {
        return Promise.reject({ message: 'Phiên đăng nhập đã hết hạn' })
      }
      this.isLogout = true
      // Clear auth token
      toast.message({
        title: 'Phiên đăng nhập đã hết hạn',
        message: 'Vui lòng đăng nhập lại!',
      })
      setTimeout(() => {
        __cAuthService.logout()
        window.location.href = '/'
      }, 2000)

      // Reject with error message
      return Promise.reject({ message: 'Phiên đăng nhập đã hết hạn' })
    }

    if (!error.response) {
      console.error('Không thể kết nối đến server')
      return Promise.reject({ message: 'Network Error' })
    }

    const status = error.response.status
    const errorMessages = {
      403: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại!',
      400: 'Có lỗi xảy ra, vui lòng thử lại sau!',
      500: 'Có lỗi server!',
      404: 'Không tìm thấy API!',
    }

    if (errorMessages[status as keyof typeof errorMessages]) {
      console.error(errorMessages[status as keyof typeof errorMessages])
    }

    return Promise.reject(error.response.data)
  }

  private handleHttpError(status: number, message?: string) {
    const errorMessages = {
      400: 'Yêu cầu không hợp lệ',
      401: 'Phiên đăng nhập đã hết hạn',
      403: 'Bạn không có quyền truy cập',
      404: 'Không tìm thấy tài nguyên',
      422: 'Dữ liệu không hợp lệ',
      429: 'Quá nhiều yêu cầu',
      500: 'Đã có lỗi xảy ra',
    }

    console.error(
      errorMessages[status as keyof typeof errorMessages] ||
        message ||
        'Đã có lỗi xảy ra'
    )
  }

  public setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  public clearAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization']
  }

  async get<T>(endpoint: string, config?: AxiosRequestConfig) {
    const url = this.buildURL(endpoint)
    return (await this.client.get<T>(url, config)).data
  }

  async post<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig) {
    const url = this.buildURL(endpoint)
    return (await this.client.post<T>(url, data, config)).data
  }

  async put<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig) {
    const url = this.buildURL(endpoint)
    return (await this.client.put<T>(url, data, config)).data
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    const url = this.buildURL(endpoint)
    return (await this.client.patch<T>(url, data, config)).data
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig) {
    const url = this.buildURL(endpoint)
    return (await this.client.delete<T>(url, config)).data
  }

  // Helper method để upload files với FormData
  async upload<T>(
    endpoint: string,
    formData: FormData,
    config?: AxiosRequestConfig
  ) {
    const url = this.buildURL(endpoint)
    const uploadConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        // Không set Content-Type để browser tự động thêm boundary
      },
    }
    return (await this.client.post<T>(url, formData, uploadConfig)).data
  }
}

export const httpClient = {
  V1: new HttpClient(EApiVersion.V1),
  V2: new HttpClient(EApiVersion.V2),
  V3: new HttpClient(EApiVersion.V3),
  V4: new HttpClient(EApiVersion.V4),
  V4_NEW: new HttpClient(EApiVersion.V4_NEW),
  DIRECT: new HttpClient(undefined, false),
}
